import { Box, Button, Center, Textarea, Stack, Text } from '@chakra-ui/react';
import type { SessionStore } from '@halo/chat-sdk-react';
import { useAppStore } from '@halo/chat-sdk-react';
import { ContentDrawer, tools } from '@halo/ui-chat-react';
import { useRefs } from '@/scripts/hooks';

export interface DescriptionCardDrawerProps {
  sessionStore: SessionStore;
  isOpen?: boolean;
  description?: string;
  disabledEditor?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  closeAllClick?: () => void;
}

const DescriptionCardDrawer = ({
  sessionStore,
  isOpen,
  description,
  disabledEditor,
  setIsOpen,
  closeAllClick,
}: DescriptionCardDrawerProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { client } = useAppStore();
  const [value, setValue] = useState(description);

  const refs = useRefs({ setIsOpen });

  const onSave = useCallback(async () => {
    const { setIsOpen } = refs.current;
    setLoading(true);
    try {
      await client?.Api.session
        .setGroupDescription(sessionStore?.session?.chat_id || '', value || '')
        .then(() => {
          tools.toaster.success({
            title: t('tips.updatedSuccess'),
          });
          setIsOpen?.(false);
        });
    } catch (error: any) {
      let errorMessage = error?.message;
      try {
        const parsed = JSON.parse(errorMessage);
        const innerError = parsed.error ? JSON.parse(parsed.error) : parsed;
        errorMessage = innerError?.message || innerError?.error;
      } catch {
        // ignore
      }
      tools.toaster.error({
        title: errorMessage || t('tips.failedToUpdateGroupDescription'),
      });
    } finally {
      setLoading(false);
    }
  }, [client?.Api.session, sessionStore?.session?.chat_id, value, refs, t]);

  useEffect(() => {
    setValue(description);
  }, [description]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen?.(open);
      if (open) {
        setValue(description);
      }
    },
    [description, setIsOpen]
  );

  return (
    <ContentDrawer
      title={t('chatSettings.editGroupDescription')}
      disabled={disabledEditor}
      open={isOpen}
      onOpenChange={onOpenChange}
      closeAllClick={closeAllClick}
    >
      <Stack pt="2" gap="34px" h="full">
        <Box>
          <Box>
            <Text textAlign="left" py="2.5" fontSize="xs" fontWeight="medium" color="fg.muted">
              {t('chatSettings.groupDescription')}
            </Text>
            <Textarea
              bg="bg"
              rows={8}
              borderRadius="xl"
              maxLength={300}
              onChange={(e) => {
                setValue(e.target.value);
              }}
              placeholder={t('chatSettings.groupDescriptionPlaceholder')}
              _placeholder={{ color: 'fg.subtle' }}
              defaultValue={value}
            />
          </Box>
        </Box>
        <Center>
          <Button
            minW="116px"
            h="10"
            borderRadius="xl"
            loading={loading}
            onClick={() => onSave()}
          >
            {t('buttons.save')}
          </Button>
        </Center>
      </Stack>
    </ContentDrawer>
  );
};

const MemoizedDescriptionCardDrawer = memo(DescriptionCardDrawer);
export { MemoizedDescriptionCardDrawer as DescriptionCardDrawer };
